// Hook for camera access and stream management

import { useState, useRef, useCallback, useEffect } from 'react'

interface UseCameraOptions {
  facingMode?: 'user' | 'environment'
  width?: number
  height?: number
}

interface UseCameraReturn {
  videoRef: React.RefObject<HTMLVideoElement>
  stream: MediaStream | null
  isReady: boolean
  error: string | null
  startCamera: () => Promise<void>
  stopCamera: () => void
  switchCamera: () => void
}

export function useCamera(options: UseCameraOptions = {}): UseCameraReturn {
  const videoRef = useRef<HTMLVideoElement>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const [isReady, setIsReady] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [facingMode, setFacingMode] = useState<'user' | 'environment'>(
    options.facingMode || 'environment'
  )

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop())
      streamRef.current = null
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null
    }
    setIsReady(false)
  }, [])

  const startCamera = useCallback(async () => {
    // Stop any existing stream first
    stopCamera()
    setError(null)

    try {
      const constraints: MediaStreamConstraints = {
        video: {
          facingMode: facingMode,
          width: { ideal: options.width || 1280 },
          height: { ideal: options.height || 720 },
        },
        audio: true,
      }

      const stream = await navigator.mediaDevices.getUserMedia(constraints)
      streamRef.current = stream

      if (videoRef.current) {
        videoRef.current.srcObject = stream
        // Wait for video to be ready
        await new Promise<void>((resolve, reject) => {
          const video = videoRef.current
          if (!video) {
            reject(new Error('Video element not available'))
            return
          }
          // If metadata already loaded, resolve immediately
          if (video.readyState >= 1) {
            resolve()
          } else {
            video.onloadedmetadata = () => resolve()
            // Timeout after 10 seconds
            setTimeout(() => reject(new Error('Video metadata load timeout')), 10000)
          }
        })
        await videoRef.current.play()
        setIsReady(true)
      } else {
        throw new Error('Video element not mounted')
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to access camera'

      // Provide helpful error messages
      if (message.includes('Permission denied') || message.includes('NotAllowedError')) {
        setError('Camera permission denied. Please allow camera access and try again.')
      } else if (message.includes('NotFoundError')) {
        setError('No camera found. Please connect a camera and try again.')
      } else {
        setError(message)
      }
    }
  }, [facingMode, options.width, options.height, stopCamera])

  const switchCamera = useCallback(() => {
    setFacingMode((prev) => (prev === 'user' ? 'environment' : 'user'))
  }, [])

  // Restart camera when facing mode changes (only if already started)
  useEffect(() => {
    if (isReady) {
      startCamera()
    }
  }, [facingMode]) // eslint-disable-line react-hooks/exhaustive-deps

  // Cleanup on unmount
  useEffect(() => {
    return () => stopCamera()
  }, [stopCamera])

  return {
    videoRef,
    stream: streamRef.current,
    isReady,
    error,
    startCamera,
    stopCamera,
    switchCamera,
  }
}
